import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMealPlans, createMealPlan, deleteMealPlan, getShoppingList, MEAL_TYPES } from '../../services/mealPlanService';
import { getRecipes } from '../../services/recipeService';

const toISODate = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); // avoid UTC day-shift
  return d.toISOString().slice(0, 10);
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MEAL_LABELS = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' };

const MealPlanner = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewScope, setViewScope] = useState('group'); // 'group' | 'mine'

  const [pickerSlot, setPickerSlot] = useState(null); // { date, mealType } | null
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const [showShoppingList, setShowShoppingList] = useState(false);
  const [shoppingList, setShoppingList] = useState([]);
  const [shoppingListLoading, setShoppingListLoading] = useState(false);
  const [hasMealsPlanned, setHasMealsPlanned] = useState(true);
  const [recipesWithoutIngredients, setRecipesWithoutIngredients] = useState([]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = weekDays[6];

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMealPlans(toISODate(weekStart), toISODate(weekEnd), viewScope);
      setEntries(data.entries);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load meal plan');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, viewScope]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const entriesFor = (date, mealType) => {
    const iso = toISODate(date);
    return entries.filter((e) => e.planned_date.slice(0, 10) === iso && e.meal_type === mealType);
  };

  const openPicker = (date, mealType) => {
    setPickerSlot({ date, mealType });
    setPickerSearch('');
    setPickerResults([]);
  };

  const closePicker = () => setPickerSlot(null);

  useEffect(() => {
    if (!pickerSlot) return;
    const timeout = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const data = await getRecipes({ search: pickerSearch, limit: 8 });
        setPickerResults(data.recipes);
      } catch (err) {
        setPickerResults([]);
      } finally {
        setPickerLoading(false);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [pickerSearch, pickerSlot]);

  const handlePickRecipe = async (recipe) => {
    try {
      await createMealPlan({
        recipe_id: recipe.id,
        planned_date: toISODate(pickerSlot.date),
        meal_type: pickerSlot.mealType,
      });
      closePicker();
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add to meal plan');
    }
  };

  const handleRemove = async (entryId) => {
    try {
      await deleteMealPlan(entryId);
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove entry');
    }
  };

  const handlePrintShoppingList = () => {
    window.print();
  };

  const openShoppingList = async () => {
    setShowShoppingList(true);
    setShoppingListLoading(true);
    try {
      const data = await getShoppingList(toISODate(weekStart), toISODate(weekEnd), viewScope);
      setShoppingList(data.shoppingList);
      setHasMealsPlanned(data.hasMealsPlanned);
      setRecipesWithoutIngredients(data.recipesWithoutIngredients || []);
    } catch (err) {
      setShoppingList([]);
      setHasMealsPlanned(true);
      setRecipesWithoutIngredients([]);
    } finally {
      setShoppingListLoading(false);
    }
  };

  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📅 Meal Planner</h2>
        <div style={styles.weekNav}>
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} style={styles.navBtn}>← Prev</button>
          <span style={styles.weekLabel}>{weekLabel}</span>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} style={styles.navBtn}>Next →</button>
          <button onClick={() => setWeekStart(getMonday(new Date()))} style={styles.todayBtn}>Today</button>
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.scopeToggle}>
          <button
            onClick={() => setViewScope('group')}
            style={viewScope === 'group' ? styles.scopeBtnActive : styles.scopeBtn}
          >
            👨‍👩‍👧 Everyone's Plan
          </button>
          <button
            onClick={() => setViewScope('mine')}
            style={viewScope === 'mine' ? styles.scopeBtnActive : styles.scopeBtn}
          >
            👤 My Plan
          </button>
        </div>
        <button onClick={openShoppingList} style={styles.shoppingListBtn}>🛒 Shopping List</button>
      </div>

      {error && <p style={styles.error}>{error}</p>}
      {loading ? (
        <p style={styles.status}>Loading meal plan...</p>
      ) : (
        <div style={styles.grid}>
          {weekDays.map((date, i) => (
            <div key={i} style={styles.dayColumn}>
              <div style={styles.dayHeader}>
                <div style={styles.dayName}>{DAY_LABELS[i]}</div>
                <div style={styles.dayDate}>{date.getDate()}</div>
              </div>
              {MEAL_TYPES.map((mealType) => (
                <div key={mealType} style={styles.mealSlot}>
                  <div style={styles.mealLabel}>{MEAL_LABELS[mealType]}</div>
                  {entriesFor(date, mealType).map((entry) => (
                    <div key={entry.id} style={styles.entryCard} onClick={() => navigate(`/recipes/${entry.recipe_id}`)}>
                      <span style={styles.entryTitle}>{entry.recipe_title}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemove(entry.id); }}
                        style={styles.entryRemoveBtn}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button onClick={() => openPicker(date, mealType)} style={styles.addSlotBtn}>+ Add</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {pickerSlot && (
        <div style={styles.modalOverlay} onClick={closePicker}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              Add to {MEAL_LABELS[pickerSlot.mealType]} — {pickerSlot.date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>
            <input
              type="text"
              placeholder="Search your recipes..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              style={styles.modalSearch}
              autoFocus
            />
            <div style={styles.modalResults}>
              {pickerLoading && <p style={styles.status}>Searching...</p>}
              {!pickerLoading && pickerResults.length === 0 && (
                <p style={styles.status}>No recipes found</p>
              )}
              {pickerResults.map((recipe) => (
                <div key={recipe.id} style={styles.modalResultItem} onClick={() => handlePickRecipe(recipe)}>
                  {recipe.photo_url ? (
                    <img src={recipe.photo_url} alt="" style={styles.modalResultImg} />
                  ) : (
                    <div style={styles.modalResultPlaceholder}>🍽️</div>
                  )}
                  <span>{recipe.title}</span>
                </div>
              ))}
            </div>
            <button onClick={closePicker} style={styles.modalCloseBtn}>Cancel</button>
          </div>
        </div>
      )}

      {showShoppingList && (
        <div style={styles.modalOverlay} onClick={() => setShowShoppingList(false)}>
          <style>{`
            @media print {
              body * { visibility: hidden; }
              .shopping-list-print-area, .shopping-list-print-area * { visibility: visible; }
              .shopping-list-print-area {
                position: absolute; left: 0; top: 0; width: 100%;
                box-shadow: none !important; max-height: none !important; padding: 20px 40px !important;
              }
              .no-print-inner { display: none !important; }
              .print-header { display: block !important; }
            }
          `}</style>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} className="shopping-list-print-area">
            <div className="print-header" style={styles.printHeader}>
              <h1 style={styles.printHeaderTitle}>GrubClub</h1>
              <p style={styles.printHeaderSubtitle}>Grocery List</p>
              <p style={styles.printHeaderMeta}>
                {weekLabel} · {viewScope === 'mine' ? 'My Plan' : "Everyone's Plan"}
              </p>
            </div>

            <div style={styles.shoppingListHeader}>
              <h3 style={styles.modalTitle} className="no-print-inner">
                🛒 Shopping List — {weekLabel} ({viewScope === 'mine' ? 'My Plan' : "Everyone's Plan"})
              </h3>
              <button onClick={handlePrintShoppingList} style={styles.printBtn} className="no-print-inner">🖨️ Print</button>
            </div>
            <div style={styles.shoppingListWrap}>
              {shoppingListLoading && <p style={styles.status}>Building your list...</p>}
              {!shoppingListLoading && !hasMealsPlanned && (
                <p style={styles.status}>No meals planned this week yet — add some recipes first!</p>
              )}
              {!shoppingListLoading && hasMealsPlanned && shoppingList.length === 0 && (
                <p style={styles.status}>
                  You have meals planned this week, but none of those recipes have ingredients listed yet.
                </p>
              )}
              {shoppingList.map((item) => (
                <div key={item.name} style={styles.shoppingRow}>
                  <span style={styles.checkbox}>☐</span>
                  <span style={styles.shoppingItemText}>
                    <strong style={styles.shoppingItemName}>{item.name}</strong>
                    {' — '}
                    {item.occurrences.map((occ, idx) => (
                      <span key={idx}>
                        {idx > 0 && ', '}
                        {[occ.amount, occ.unit].filter(Boolean).join(' ')} ({occ.recipeTitle})
                      </span>
                    ))}
                  </span>
                </div>
              ))}
              {!shoppingListLoading && recipesWithoutIngredients.length > 0 && (
                <p style={styles.noIngredientsNote} className="no-print-inner">
                  ⚠️ No ingredients listed for: {recipesWithoutIngredients.join(', ')}
                </p>
              )}
            </div>
            <button onClick={() => setShowShoppingList(false)} style={styles.modalCloseBtn} className="no-print-inner">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {},
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  title: { color: '#333', margin: 0 },
  weekNav: { display: 'flex', alignItems: 'center', gap: '10px' },
  navBtn: { padding: '6px 12px', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  todayBtn: { padding: '6px 12px', backgroundColor: '#fdf0e3', color: '#e67e22', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  weekLabel: { fontWeight: '600', color: '#555', fontSize: '14px', minWidth: '140px', textAlign: 'center' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
  scopeToggle: { display: 'flex', backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '4px', gap: '4px' },
  scopeBtn: {
    padding: '8px 14px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', color: '#666', fontWeight: '600',
  },
  scopeBtnActive: {
    padding: '8px 14px', backgroundColor: 'white', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', color: '#e67e22', fontWeight: '600', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  shoppingListBtn: {
    padding: '8px 16px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px', overflowX: 'auto' },
  dayColumn: { backgroundColor: 'white', borderRadius: '10px', padding: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', minWidth: '140px' },
  dayHeader: { textAlign: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' },
  dayName: { fontSize: '12px', color: '#999', fontWeight: '600', textTransform: 'uppercase' },
  dayDate: { fontSize: '18px', color: '#333', fontWeight: '700' },
  mealSlot: { marginBottom: '10px' },
  mealLabel: { fontSize: '11px', color: '#e67e22', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' },
  entryCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: '#fdf0e3', borderRadius: '6px', padding: '6px 8px', marginBottom: '4px',
    fontSize: '12px', cursor: 'pointer',
  },
  entryTitle: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  entryRemoveBtn: { background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '11px', marginLeft: '6px' },
  addSlotBtn: {
    width: '100%', padding: '4px', background: 'transparent', border: '1px dashed #ddd',
    borderRadius: '6px', color: '#999', fontSize: '11px', cursor: 'pointer',
  },
  status: { textAlign: 'center', color: '#999' },
  error: { textAlign: 'center', color: '#e74c3c' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '420px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' },
  modalTitle: { fontSize: '16px', color: '#333', marginBottom: '14px' },
  shoppingListHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },
  printBtn: {
    padding: '8px 14px', backgroundColor: 'white', color: '#e67e22', border: '1px solid #e67e22',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  modalSearch: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '12px' },
  modalResults: { flex: 1, overflowY: 'auto', marginBottom: '14px' },
  modalResultItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '8px', cursor: 'pointer' },
  modalResultImg: { width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' },
  modalResultPlaceholder: { width: '36px', height: '36px', borderRadius: '6px', backgroundColor: '#fdf0e3', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalCloseBtn: { padding: '10px', backgroundColor: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#666' },
  shoppingListWrap: { flex: 1, overflowY: 'auto', marginBottom: '14px' },
  shoppingRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 0', borderBottom: '1px solid #f7f7f7' },
  checkbox: { fontSize: '15px', color: '#bbb', lineHeight: 1.4 },
  shoppingItemText: { fontSize: '13px', color: '#444', lineHeight: 1.4 },
  shoppingItemName: { color: '#333', textTransform: 'capitalize' },
  noIngredientsNote: { fontSize: '12px', color: '#e67e22', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f5f5f5' },
  printHeader: { display: 'none', textAlign: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e67e22' },
  printHeaderTitle: { color: '#e67e22', fontSize: '26px', margin: '0 0 2px 0', fontFamily: 'Georgia, serif' },
  printHeaderSubtitle: { color: '#333', fontSize: '16px', fontWeight: '600', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '1px' },
  printHeaderMeta: { color: '#888', fontSize: '13px', margin: 0 },
};

export default MealPlanner;
